import { useEffect } from 'react'
import { useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import type { Id } from '../../../convex/_generated/dataModel'
import { useAuthStore } from '../../stores/authStore'
import { useFamilyStore, type Family, type FamilyMember, type Child } from '../../stores/familyStore'

/**
 * Syncs Convex reactive queries → FamilyStore (Zustand).
 *
 * Placed in App.tsx alongside ClerkAuthSync. Convex queries are reactive:
 * when data changes on the server, this component automatically re-renders
 * and pushes fresh data to the Zustand store. All components that read from
 * useFamilyStore() get the latest data without manual fetching.
 */
export function FamilyDataSync() {
  const { user } = useAuthStore()
  const { setFamily, setMembers, setChildren, setInitialized, setIsLoading } = useFamilyStore()

  // Get user's family ID (reactive)
  const familyId = useQuery(
    api.families.queries.getUserFamilyIdQuery
  )
  // familyId: undefined (loading) | null (no family) | Id<"families">

  // Get family data (reactive, skipped if no familyId)
  const familyData = useQuery(
    api.families.queries.getFamily,
    familyId ? { familyId: familyId as Id<"families"> } : "skip"
  )

  // Get family members (reactive)
  const membersData = useQuery(
    api.families.queries.getFamilyMembers,
    familyId ? { familyId: familyId as Id<"families"> } : "skip"
  )

  // Get children (reactive)
  const childrenData = useQuery(
    api.families.queries.getChildren,
    familyId ? { familyId: familyId as Id<"families"> } : "skip"
  )

  // Sync familyId resolution
  useEffect(() => {
    if (!user) {
      // Not logged in - reset
      setFamily(null)
      setMembers([])
      setChildren([])
      setInitialized(true)
      return
    }

    if (familyId === undefined) {
      // Still loading
      setIsLoading(true)
      return
    }

    if (familyId === null) {
      // User has no family
      setFamily(null)
      setMembers([])
      setChildren([])
      setIsLoading(false)
      setInitialized(true)
      return
    }

    // familyId is valid — data will come from individual queries below
  }, [user, familyId, setFamily, setMembers, setChildren, setInitialized, setIsLoading])

  // Sync family data
  useEffect(() => {
    if (familyData === undefined) return // still loading

    if (familyData === null) {
      setFamily(null)
      return
    }

    const mapped: Family = {
      id: familyData._id,
      name: familyData.name,
      home_a_name: familyData.homeAName,
      home_b_name: familyData.homeBName,
      created_at: new Date(familyData._creationTime).toISOString(),
      encryption_active_since: familyData.encryptionActiveSince ?? null,
    }
    setFamily(mapped)
  }, [familyData, setFamily])

  // Sync members
  useEffect(() => {
    if (membersData === undefined) return

    const mapped: FamilyMember[] = membersData.map((m: any) => ({
      id: m._id,
      user_id: m.userId,
      family_id: m.familyId,
      role: m.role,
      joined_at: m.joinedAt ? new Date(m.joinedAt).toISOString() : null,
      invited_at: m.invitedAt ? new Date(m.invitedAt).toISOString() : null,
      user_email: m.userEmail ?? undefined,
    }))
    setMembers(mapped)
  }, [membersData, setMembers])

  // Sync children
  useEffect(() => {
    if (childrenData === undefined) return

    const mapped: Child[] = childrenData.map((c: any) => ({
      id: c._id,
      family_id: c.familyId,
      name: c.name,
      birth_date: c.birthDate ?? null,
      created_at: new Date(c._creationTime).toISOString(),
    }))
    setChildren(mapped)
  }, [childrenData, setChildren])

  // Mark as initialized once all queries have loaded
  useEffect(() => {
    if (familyId === undefined) return // familyId still loading

    if (familyId === null) {
      // No family — already handled above
      return
    }

    // Family exists: wait for all data to load
    if (familyData !== undefined && membersData !== undefined && childrenData !== undefined) {
      setIsLoading(false)
      setInitialized(true)
    }
  }, [familyId, familyData, membersData, childrenData, setIsLoading, setInitialized])

  return null // Renderless component
}
